import { useCallback } from 'react';
import { productsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Product } from '../types';

export function useProducts(
  options?: ResourceQueryOptions
): ResourceState<Product> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      productsService.getAll(opts) as unknown as Promise<{ data: Product[]; meta?: any }>,
    []
  );
  return useResourceCollection<Product>(fetchFn, options);
}

export function useProduct(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Product> {
  const resourceId = id;
  const fetchFn = useCallback(
    (productId: string | number, opts?: ResourceQueryOptions) =>
      productsService.getById(productId, opts) as unknown as Promise<Product>,
    []
  );
  return useResourceById<Product>(fetchFn, resourceId, options);
}

export function useCreateProduct(
  onSuccess?: (data: Product) => void
): MutationState<Product> {
  const createFn = useCallback(
    (data: any) => productsService.create(data) as unknown as Promise<Product>,
    []
  );
  return useCreateResource<Product>(createFn, onSuccess);
}

export function useUpdateProduct(
  onSuccess?: (data: Product) => void
): MutationState<Product> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      return productsService.update(id, data) as unknown as Promise<Product>;
    },
    []
  );
  return useUpdateResource<Product>(updateFn, onSuccess);
}

/**
 * Delete product using documentId
 * @param data - Full product object containing documentId
 */
export function useDeleteProduct(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return productsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

